import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Trash2, Lock, Settings, X } from 'lucide-react';
import Dropdown from '../components/receptura/Dropdown';
import LayerHeader from '../components/ui/LayerHeader';
import { NumCell } from '../components/ui/NumCell';
import { TH, makeSticky } from '../components/ui/gridTable';
import { fmt, num, fmtKc, MESICE_SHORT, MKEYS } from '../lib/formatters';
import { ACCENT, WARN, W_MESIC, tableSurfaces, OK, VIOLET, BAD } from '../lib/uiTokens';
import { AopTable, BtnUlozit, LayerSpinner } from '../components/ui/aopUi';
import useSirokyDisplej from '../hooks/useSirokyDisplej';

const DNY = [
    ['hod_po', 'Po'], ['hod_ut', 'Út'], ['hod_st', 'St'], ['hod_ct', 'Čt'],
    ['hod_pa', 'Pá'], ['hod_so', 'So'], ['hod_ne', 'Ne'], ['hod_svatek', 'Svátek'],
];

// Šířky sloupců + sticky offsety levého bloku (Pozice → Roční)
const W = { poz: 200, uvazek: 88, zaklad: 128, rocni: 126, mesic: W_MESIC, akce: 60 };
const LEFT = { poz: 0, uvazek: W.poz, zaklad: W.poz + W.uvazek, rocni: W.poz + W.uvazek + W.zaklad };

// ── Kalendář: české státní svátky (fixní + Velký pátek + Velikonoční pondělí) ──
function easterSunday(Y) {
    const a = Y % 19, b = Math.floor(Y / 100), c = Y % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mo = Math.floor((h + l - 7 * m + 114) / 31), da = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Y, mo - 1, da);
}
const mmdd = (d) => `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function czSvatky(year) {
    const s = new Set(['01-01', '05-01', '05-08', '07-05', '07-06', '09-28', '10-28', '11-17', '12-24', '12-25', '12-26']);
    const e = easterSunday(year);
    const vp = new Date(e); vp.setDate(e.getDate() - 2);
    const vpo = new Date(e); vpo.setDate(e.getDate() + 1);
    s.add(mmdd(vp)); s.add(mmdd(vpo));
    return s;
}

const parseISO = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
    return (y && m && d) ? new Date(y, m - 1, d) : null;
};
function rowIntervals(r) {
    return (Array.isArray(r.intervaly) ? r.intervaly : [])
        .map(iv => ({ od: parseISO(iv.od), do: parseISO(iv.do) }))
        .filter(iv => iv.od && iv.do);
}
function dayOccupied(dt, intervals) {
    if (!intervals.length) return true;
    return intervals.some(iv => dt >= iv.od && dt <= iv.do);
}

// Aktivní měsíce — místo denních intervalů plánujeme po celých měsících.
// Ukládá se dál do `intervaly` jako celoměsíční rozsahy (prázdné = celý rok).
const ODVODY_PCT = 33.8;
function monthInterval(rok, m) {
    const dim = new Date(rok, m, 0).getDate();
    const mm = String(m).padStart(2, '0');
    return { od: `${rok}-${mm}-01`, do: `${rok}-${mm}-${String(dim).padStart(2, '0')}` };
}
function checkedMonths(r, rok) {
    const iv = rowIntervals(r);
    const set = new Set();
    if (!iv.length) { for (let m = 1; m <= 12; m++) set.add(m); return set; } // prázdné = celý rok
    for (let m = 1; m <= 12; m++) {
        const dim = new Date(rok, m, 0).getDate();
        for (let d = 1; d <= dim; d++) { if (dayOccupied(new Date(rok, m - 1, d), iv)) { set.add(m); break; } }
    }
    return set;
}

// Přepočet měsíců: mzda × (1 + odvody%) + prémie (prorata dle obsazení). Prémie mimo odvody.
function recalcMonths(r, rok, svatky) {
    const intervals = rowIntervals(r);
    const odvMult = 1 + num(r.odvody_procento) / 100;
    const premie = num(r.premie);
    const isFix = r.rezim === 'fix';
    const hWd = [num(r.hod_po), num(r.hod_ut), num(r.hod_st), num(r.hod_ct), num(r.hod_pa), num(r.hod_so), num(r.hod_ne)];
    const hSv = num(r.hod_svatek);
    const out = Array(12).fill(0);
    for (let mo = 0; mo < 12; mo++) {
        const dim = new Date(rok, mo + 1, 0).getDate();
        let occDays = 0, hrs = 0;
        for (let d = 1; d <= dim; d++) {
            const dt = new Date(rok, mo, d);
            if (!dayOccupied(dt, intervals)) continue;
            occDays++;
            if (!isFix) hrs += svatky.has(`${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`) ? hSv : hWd[(dt.getDay() + 6) % 7];
        }
        const occFrac = occDays / dim;
        const mzda = isFix ? num(r.mesicni_castka) * occFrac : num(r.sazba_kc_h) * hrs;
        out[mo] = Math.round((mzda * odvMult + premie * occFrac) * 100) / 100;
    }
    return out;
}

// ── Modal: parametry pozice ──────────────────────────────────
function ModalNum({ value, onChange, w = 'w-24', suffix, placeholder = '0' }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <input type="text" inputMode="numeric" value={value ?? ''} placeholder={placeholder}
                onChange={e => onChange(e.target.value.replace(/[^\d.]/g, ''))}
                className={`${w} bg-transparent text-right text-[14px] rounded-md px-2 py-1.5 outline-none tabular-nums focus:ring-1 focus:ring-blue-500/60`}
                style={{ color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
            {suffix && <span className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>{suffix}</span>}
        </span>
    );
}

function PositionModal({ r, typy, kariery, rok, isDark, cardStyle, onPatch, onToggleMonth, onDelete, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    const isFix = r.rezim === 'fix';
    const checked = checkedMonths(r, Number(rok));
    const odvodyOn = num(r.odvody_procento) > 0;
    const seg = (active) => ({ background: active ? ACCENT : 'transparent', color: active ? '#fff' : 'var(--text-secondary)' });
    const label = { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)' };

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()}
                style={{ width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', borderRadius: 12,
                    background: isDark ? 'rgba(20,20,30,0.98)' : 'var(--surface-raised)', border: '1px solid var(--border-default)', boxShadow: '0 25px 80px rgba(0,0,0,0.5)' }}>
                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <Settings size={16} style={{ color: ACCENT }} />
                    <span className="text-[15px] font-bold flex-1" style={{ color: 'var(--text-primary)' }}>Nastavení pozice</span>
                    <button onClick={onClose} className="p-1 rounded-lg" style={{ color: 'var(--text-tertiary)' }}><X size={16} /></button>
                </div>

                <div className="px-5 py-4 space-y-4">
                    {/* Název */}
                    <div>
                        <div style={label} className="mb-1">Název</div>
                        <input value={r.nazev} onChange={e => onPatch({ nazev: e.target.value })}
                            className="w-full bg-transparent rounded-md px-2 py-2 text-[15px] font-semibold outline-none focus:ring-1 focus:ring-blue-500/60"
                            style={{ color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} placeholder="Název pozice" />
                    </div>

                    {/* Úvazek + Kariéra */}
                    <div className="flex gap-3 flex-wrap">
                        <div className="flex-1 min-w-[160px]">
                            <div style={label} className="mb-1">Úvazek</div>
                            <Dropdown value={r.typ_uvazku_id || ''} onChange={v => onPatch({ typ_uvazku_id: v || null })}
                                options={[{ value: '', label: '— úvazek —' }, ...typy.map(t => ({ value: t.id, label: t.kod }))]}
                                placeholder="Úvazek" isDark={isDark} accent={ACCENT} height={38} minWidth={160} />
                        </div>
                        <div className="flex-1 min-w-[160px]">
                            <div style={label} className="mb-1">Kariéra</div>
                            <Dropdown value={r.kariera_id || ''} onChange={v => onPatch({ kariera_id: v || null })}
                                options={[{ value: '', label: '— kariéra —' }, ...kariery.map(k => ({ value: k.id, label: k.nazev }))]}
                                placeholder="Kariéra" isDark={isDark} accent={ACCENT} height={38} minWidth={160} />
                        </div>
                    </div>

                    {/* Režim + driver */}
                    <div>
                        <div style={label} className="mb-1.5">Mzda</div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                                <button onClick={() => onPatch({ rezim: 'fix' })} className="px-3 py-1.5 text-xs font-bold" style={seg(isFix)}>FIX</button>
                                <button onClick={() => onPatch({ rezim: 'var' })} className="px-3 py-1.5 text-xs font-bold" style={{ ...seg(!isFix), borderLeft: '1px solid var(--border-default)' }}>VAR</button>
                            </div>
                            {isFix
                                ? <ModalNum value={r.mesicni_castka} onChange={v => onPatch({ mesicni_castka: v })} w="w-32" suffix="Kč / měs" />
                                : <ModalNum value={r.sazba_kc_h} onChange={v => onPatch({ sazba_kc_h: v })} w="w-20" suffix="Kč / h" />}
                        </div>
                        {!isFix && (
                            <div className="flex items-center gap-2 flex-wrap mt-3">
                                <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Hodiny/den:</span>
                                {DNY.map(([k, lbl]) => (
                                    <label key={k} className="inline-flex flex-col items-center">
                                        <span className="text-[10px]" style={{ color: k === 'hod_svatek' ? BAD : 'var(--text-tertiary)' }}>{lbl}</span>
                                        <ModalNum value={r[k]} onChange={v => onPatch({ [k]: v })} w="w-12" />
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Odvody + Prémie */}
                    <div className="flex gap-6 flex-wrap items-start">
                        <div>
                            <div style={label} className="mb-1">Odvody</div>
                            <label className="inline-flex items-center gap-2 cursor-pointer text-[14px] py-1.5" style={{ color: 'var(--text-secondary)' }}>
                                <input type="checkbox" checked={odvodyOn}
                                    onChange={e => onPatch({ odvody_procento: e.target.checked ? ODVODY_PCT : 0 })}
                                    style={{ width: 16, height: 16, accentColor: ACCENT }} />
                                Počítat 33,8 % z hrubé mzdy
                            </label>
                        </div>
                        <div>
                            <div style={label} className="mb-1">Prémie</div>
                            <ModalNum value={r.premie} onChange={v => onPatch({ premie: v })} w="w-28" suffix="Kč / měs" />
                        </div>
                    </div>

                    {/* Aktivní měsíce */}
                    <div>
                        <div style={label} className="mb-1.5">Aktivní měsíce</div>
                        <div className="flex flex-wrap gap-1.5">
                            {MESICE_SHORT.map((lbl, i) => {
                                const m = i + 1;
                                const on = checked.has(m);
                                return (
                                    <button key={m} onClick={() => onToggleMonth(m)}
                                        className="px-2.5 py-1.5 rounded-lg text-[12px] font-medium"
                                        style={on
                                            ? { background: ACCENT, color: '#fff', border: `1px solid ${ACCENT}` }
                                            : { background: 'transparent', color: 'var(--text-tertiary)', border: '1px solid var(--border-default)' }}>
                                        {lbl}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="text-[11px] mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
                            {checked.size === 12 ? 'Celý rok — aktivní všechny měsíce' : `Aktivní ${checked.size} z 12 měsíců`}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center px-5 py-3" style={{ borderTop: '1px solid var(--border-default)' }}>
                    <button onClick={() => { onDelete(); }} className="inline-flex items-center gap-1.5 text-[13px] font-medium" style={{ color: BAD }}>
                        <Trash2 size={14} /> Smazat pozici
                    </button>
                    <div className="flex-1" />
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: ACCENT }}>Hotovo</button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default function FinanceAopPersonal({ rok, variantaId, cardStyle, isDark }) {
    const siroky = useSirokyDisplej();
    const [rows, setRows] = useState([]);
    const [typy, setTypy] = useState([]);
    const [kariery, setKariery] = useState([]);
    const [dirty, setDirty] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modalId, setModalId] = useState(null);

    const svatky = useMemo(() => czSvatky(Number(rok)), [rok]);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const headers = { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token };
    const qVar = variantaId ? `&varianta_id=${variantaId}` : '';

    const sortRows = useCallback((list, karList) => {
        const kmap = Object.fromEntries((karList || []).map(k => [k.id, k.nazev]));
        return list.slice().sort((a, b) => {
            const ka = a.kariera_id ? (kmap[a.kariera_id] || '') : '￿';
            const kb = b.kariera_id ? (kmap[b.kariera_id] || '') : '￿';
            return ka.localeCompare(kb, 'cs') || (a.nazev || '').localeCompare(b.nazev || '', 'cs');
        });
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetch(`/api/finance/aop/personal?rok=${rok}${qVar}`).then(r => r.json());
            setRows(sortRows(d.radky || [], d.kariery || []));
            setTypy(d.typy_uvazku || []);
            setKariery(d.kariery || []);
            setDirty(new Set());
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [rok, variantaId, sortRows]);

    useEffect(() => { loadData(); }, [loadData]);

    const markDirty = (id) => setDirty(prev => new Set(prev).add(id));

    const patchRow = (id, patch, recompute = true) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const next = { ...r, ...patch };
            if (recompute && !next.zamceno) {
                const m = recalcMonths(next, Number(rok), svatky);
                MKEYS.forEach((k, i) => { next[k] = m[i]; });
            }
            return next;
        }));
        markDirty(id);
    };

    // Ruční úprava měsíce → zamkne řádek proti přepočtu
    const setMesic = (id, k, value) => {
        setRows(prev => prev.map(r => (r.id === id ? { ...r, [k]: value, zamceno: true } : r)));
        markDirty(id);
    };

    const toggleLock = (id) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const zamceno = !r.zamceno;
            const next = { ...r, zamceno };
            if (!zamceno) { const m = recalcMonths(next, Number(rok), svatky); MKEYS.forEach((k, i) => { next[k] = m[i]; }); }
            return next;
        }));
        markDirty(id);
    };

    // Přepnutí aktivního měsíce (ukládá se jako celoměsíční rozsahy do intervaly; prázdné = celý rok)
    const toggleMonth = (id, m) => {
        const r = rows.find(x => x.id === id); if (!r) return;
        const cur = checkedMonths(r, Number(rok));
        cur.has(m) ? cur.delete(m) : cur.add(m);
        if (cur.size === 0) return; // aspoň jeden měsíc musí zůstat
        const list = cur.size === 12 ? [] : [...cur].sort((a, b) => a - b).map(mm => monthInterval(Number(rok), mm));
        patchRow(id, { intervaly: list });
    };

    const handleAdd = async () => {
        try {
            const created = await fetch('/api/finance/aop/personal', {
                method: 'POST', headers, body: JSON.stringify({ rok, varianta_id: variantaId, nazev: 'Nová pozice' }),
            }).then(r => r.json());
            setRows(prev => sortRows([...prev, created], kariery));
            setModalId(created.id); // rovnou otevřít nastavení
        } catch (e) { alert('Chyba'); }
    };

    const handleDelete = async (id) => {
        if (!confirm('Smazat pozici?')) return;
        try {
            await fetch(`/api/finance/aop/personal/${id}`, { method: 'DELETE', headers });
            setRows(prev => prev.filter(r => r.id !== id));
            setDirty(prev => { const n = new Set(prev); n.delete(id); return n; });
            setModalId(null);
        } catch (e) { alert('Chyba'); }
    };

    const rowBody = (r) => ({
        nazev: r.nazev, typ_uvazku_id: r.typ_uvazku_id || null, kariera_id: r.kariera_id || null,
        rezim: r.rezim || 'var', mesicni_castka: num(r.mesicni_castka), sazba_kc_h: num(r.sazba_kc_h),
        odvody_procento: num(r.odvody_procento), premie: num(r.premie),
        ...Object.fromEntries(DNY.map(([k]) => [k, num(r[k])])),
        intervaly: Array.isArray(r.intervaly) ? r.intervaly : [],
        zamceno: !!r.zamceno, poznamka: r.poznamka || null,
        ...Object.fromEntries(MKEYS.map(k => [k, num(r[k])])),
    });

    const handleUlozit = async () => {
        if (dirty.size === 0) return;
        setSaving(true);
        try {
            await Promise.all([...dirty].map(id => {
                const r = rows.find(x => x.id === id);
                return r ? fetch(`/api/finance/aop/personal/${id}`, { method: 'PUT', headers, body: JSON.stringify(rowBody(r)) }) : null;
            }));
            loadData();
        } catch (e) { alert('Chyba při ukládání'); }
        setSaving(false);
    };

    if (loading) {
        return <LayerSpinner />;
    }

    const rocni = (r) => MKEYS.reduce((s, k) => s + num(r[k]), 0);
    const celkemM = MKEYS.map(k => rows.reduce((s, r) => s + num(r[k]), 0));
    const celkemRok = celkemM.reduce((a, b) => a + b, 0);
    const karNazev = (id) => kariery.find(k => k.id === id)?.nazev;
    const uvazekKod = (id) => typy.find(t => t.id === id)?.kod;
    const zakladLabel = (r) => r.rezim === 'fix' ? `${fmt(r.mesicni_castka)} /měs` : `${fmt(r.sazba_kc_h)} Kč/h`;

    // Seskupení dle kariéry (řádky už jsou seřazené)
    const groups = [];
    rows.forEach(r => {
        const key = r.kariera_id || 0;
        let g = groups.find(x => x.key === key);
        if (!g) { g = { key, nazev: karNazev(r.kariera_id) || 'Bez kariéry', rows: [], mSum: Array(12).fill(0) }; groups.push(g); }
        g.rows.push(r);
        MKEYS.forEach((k, i) => { g.mSum[i] += num(r[k]); });
    });

    const { stickyBg, headBg, groupBg } = tableSurfaces(isDark);
    const th = TH;
    const sticky = makeSticky(LEFT, 'rocni', siroky ? null : ['poz']);

    const modalRow = modalId != null ? rows.find(r => r.id === modalId) : null;

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Hlavička vrstvy */}
            <LayerHeader krok={3} title="Personální náklady" cardStyle={cardStyle}>
                Pozice tříděné dle kariéry. Řádek je jen ke čtení — parametry (úvazek, mzda, odvody, prémie, aktivní měsíce) se nastaví přes <b>⚙</b>.
                Přímo v tabulce se dají upravit jen <b>měsíční částky</b> (tím se řádek zamkne proti přepočtu). Měsíc = mzda × (1 + odvody %) + prémie. Mzdy jsou bez DPH.
            </LayerHeader>

            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap flex-shrink-0">
                <button onClick={handleAdd} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>
                    <Plus size={14} /> Přidat pozici
                </button>
                <div className="flex-1" />
                <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    Personál celkem: <b style={{ color: 'var(--text-primary)' }}>{fmtKc(celkemRok)}</b> / rok
                </div>
                <BtnUlozit onClick={handleUlozit} saving={saving} dirtyCount={dirty.size} />
            </div>

            {/* Tabulka */}
            <AopTable cardStyle={cardStyle}>
                        <colgroup>
                            <col style={{ width: W.poz }} />
                            <col style={{ width: W.uvazek }} />
                            <col style={{ width: W.zaklad }} />
                            <col style={{ width: W.rocni }} />
                            {MKEYS.map(k => <col key={k} style={{ width: W.mesic }} />)}
                            <col style={{ width: W.akce }} />
                        </colgroup>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                <th className={`${th} text-left`} style={{ ...sticky('poz', 30, headBg), color: 'var(--text-secondary)' }}>Pozice</th>
                                <th className={`${th} text-left`} style={{ ...sticky('uvazek', 30, headBg), color: 'var(--text-tertiary)' }}>Úvazek</th>
                                <th className={`${th} text-right`} style={{ ...sticky('zaklad', 30, headBg), color: 'var(--text-tertiary)' }}>Základ</th>
                                <th className={`${th} text-right`} style={{ ...sticky('rocni', 30, headBg), color: ACCENT }}>Roční</th>
                                {MESICE_SHORT.map((m, i) => <th key={i} className={`${th} text-right`} style={{ color: 'var(--text-tertiary)', background: headBg }}>{m}</th>)}
                                <th className={th} style={{ background: headBg }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map(g => (
                                <Fragment key={g.key}>
                                    {/* Hlavička skupiny (kariéra) + mezisoučty */}
                                    <tr>
                                        <td className="px-3 py-1.5 text-[12px] font-bold uppercase tracking-wide" style={{ ...sticky('poz', 10, groupBg), color: 'var(--text-secondary)', borderTop: '1px solid var(--border-default)' }}>{g.nazev}</td>
                                        <td style={{ ...sticky('uvazek', 10, groupBg), borderTop: '1px solid var(--border-default)' }} />
                                        <td style={{ ...sticky('zaklad', 10, groupBg), borderTop: '1px solid var(--border-default)' }} />
                                        <td className="px-2 py-1.5 text-right text-[12px] font-bold font-mono tabular-nums" style={{ ...sticky('rocni', 10, groupBg), color: 'var(--text-primary)', borderTop: '1px solid var(--border-default)' }}>{fmt(g.mSum.reduce((a, b) => a + b, 0))}</td>
                                        {g.mSum.map((v, i) => <td key={i} className="px-2 py-1.5 text-right text-[11px] font-mono tabular-nums" style={{ background: groupBg, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border-default)' }}>{v ? fmt(v) : ''}</td>)}
                                        <td style={{ background: groupBg, borderTop: '1px solid var(--border-default)' }} />
                                    </tr>
                                    {/* Řádky pozic */}
                                    {g.rows.map(r => (
                                        <tr key={r.id} className="group">
                                            <td className="px-3 py-1 cursor-pointer" style={{ ...sticky('poz', 10, stickyBg) }} onClick={() => setModalId(r.id)}>
                                                <span className="text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>{r.nazev || <span style={{ color: 'var(--text-tertiary)' }}>Bez názvu</span>}</span>
                                            </td>
                                            <td className="px-2 py-1 text-left text-[13px] cursor-pointer" style={{ ...sticky('uvazek', 10, stickyBg), color: 'var(--text-secondary)' }} onClick={() => setModalId(r.id)}>
                                                {uvazekKod(r.typ_uvazku_id) || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                                                <span className="ml-1.5 text-[10px] font-bold" style={{ color: r.rezim === 'fix' ? OK : VIOLET }}>{(r.rezim || 'var').toUpperCase()}</span>
                                            </td>
                                            <td className="px-2 py-1 text-right text-[13px] cursor-pointer tabular-nums" style={{ ...sticky('zaklad', 10, stickyBg), color: 'var(--text-secondary)' }} onClick={() => setModalId(r.id)}>{zakladLabel(r)}</td>
                                            <td className="px-2 py-1 text-right text-[13px] font-semibold font-mono tabular-nums" style={{ ...sticky('rocni', 10, stickyBg), color: r.zamceno ? WARN : 'var(--text-primary)' }}>{fmt(rocni(r))}</td>
                                            {MKEYS.map(k => (
                                                <td key={k} className="px-0.5"><NumCell value={r[k]} onChange={v => setMesic(r.id, k, v)} sm color={r.zamceno ? WARN : 'var(--text-primary)'} /></td>
                                            ))}
                                            <td className="text-center">
                                                <div className="inline-flex items-center gap-0.5">
                                                    {r.zamceno && (
                                                        <button onClick={() => toggleLock(r.id)} title="Odemknout (povolit přepočet)" className="p-1"><Lock size={13} style={{ color: WARN }} /></button>
                                                    )}
                                                    <button onClick={() => setModalId(r.id)} title="Nastavení pozice" className="p-1 opacity-70 hover:opacity-100"><Settings size={14} style={{ color: 'var(--text-tertiary)' }} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={4 + 12 + 1} className="px-4 py-10 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>Zatím žádné pozice — přidej první tlačítkem „Přidat pozici".</td></tr>
                            )}
                        </tbody>
                        {rows.length > 0 && (
                            <tfoot>
                                <tr style={{ borderTop: '2px solid var(--border-default)' }}>
                                    <td className="px-3 py-2 text-[12px] font-bold uppercase tracking-wide" style={{ ...sticky('poz', 20, headBg), color: 'var(--text-secondary)' }}>Celkem</td>
                                    <td style={{ ...sticky('uvazek', 20, headBg) }} />
                                    <td style={{ ...sticky('zaklad', 20, headBg) }} />
                                    <td className="px-2 py-2 text-right text-[13px] font-bold font-mono tabular-nums" style={{ ...sticky('rocni', 20, headBg), color: ACCENT }}>{fmtKc(celkemRok)}</td>
                                    {celkemM.map((v, i) => <td key={i} className="px-2 py-2 text-right text-[12px] font-semibold font-mono tabular-nums" style={{ background: headBg, color: 'var(--text-primary)' }}>{fmtKc(v)}</td>)}
                                    <td style={{ background: headBg }} />
                                </tr>
                            </tfoot>
                        )}
                    </AopTable>

            {modalRow && (
                <PositionModal r={modalRow} typy={typy} kariery={kariery} rok={rok} isDark={isDark} cardStyle={cardStyle}
                    onPatch={(patch) => patchRow(modalRow.id, patch)}
                    onToggleMonth={(m) => toggleMonth(modalRow.id, m)}
                    onDelete={() => handleDelete(modalRow.id)}
                    onClose={() => setModalId(null)} />
            )}
        </div>
    );
}
